import eventsData from '../content/events/events.json';

// Eagerly load all markdown event writeups. Supports both flat files
// (/src/content/events/*.md) and structured folders (/src/content/events/<slug>/main.md).
const eventMarkdownFiles = import.meta.glob(
  [
    '/src/content/events/*.md',
    '/src/content/events/*/main.md',
    '/src/content/events/*/index.md',
  ],
  {
    eager: true,
    query: '?raw',
    import: 'default',
  },
);

function parseFrontmatter(raw) {
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/);
  if (!match) {
    return { data: {}, content: raw };
  }

  const rawYaml = match[1];
  const content = match[2];
  const data = {};

  for (const line of rawYaml.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;
    const colonIdx = trimmed.indexOf(':');
    if (colonIdx === -1) continue;

    const key = trimmed.slice(0, colonIdx).trim();
    let val = trimmed.slice(colonIdx + 1).trim();

    if (
      (val.startsWith("'") && val.endsWith("'")) ||
      (val.startsWith('"') && val.endsWith('"'))
    ) {
      val = val.slice(1, -1);
    } else if (val.startsWith('[') && val.endsWith(']')) {
      val = val
        .slice(1, -1)
        .split(',')
        .map((item) => item.trim().replace(/^['"]|['"]$/g, ''))
        .filter(Boolean);
    } else if (val === 'true') {
      val = true;
    } else if (val === 'false') {
      val = false;
    }

    data[key] = val;
  }

  return { data, content };
}

function extractSlugAndBaseDir(filePath) {
  const parts = filePath.split('/');
  const fileName = parts.pop();
  if (fileName === 'main.md' || fileName === 'index.md') {
    const slug = parts[parts.length - 1];
    return {
      slug,
      baseDir: parts.join('/'),
      isFolder: true,
    };
  }
  const slug = fileName.replace(/\.md$/, '');
  return {
    slug,
    baseDir: parts.join('/'),
    isFolder: false,
  };
}

/**
 * Map of markdown details keyed by event slug.
 */
const markdownBySlug = {};
for (const [filePath, rawContent] of Object.entries(eventMarkdownFiles)) {
  const { slug: pathSlug, baseDir } = extractSlugAndBaseDir(filePath);
  const { data, content } = parseFrontmatter(rawContent);
  const slug = data.slug || pathSlug;
  markdownBySlug[slug] = {
    frontmatter: data,
    content,
    baseDir,
    filePath,
  };
}

export function getAllEvents() {
  return [...eventsData].map((event) => ({
    ...event,
    hasMarkdown: Boolean(markdownBySlug[event.slug]),
  }));
}

/**
 * Ongoing events: marked as status === 'ongoing', or current date is between date and endDate
 */
export function getOngoingEvents(today = new Date()) {
  const todayIso = today.toISOString().slice(0, 10);
  return getAllEvents().filter((e) => {
    if (e.status === 'ongoing') return true;
    if (e.status === 'completed') return false;
    if (e.date <= todayIso && e.endDate && e.endDate >= todayIso) return true;
    if (e.date === todayIso && !e.status) return true;
    return false;
  });
}

/**
 * Upcoming events: scheduled for future dates and not ongoing or completed
 */
export function getUpcomingEvents(today = new Date()) {
  const todayIso = today.toISOString().slice(0, 10);
  const ongoingSlugs = new Set(getOngoingEvents(today).map((e) => e.slug));

  return getAllEvents()
    .filter((e) => {
      if (ongoingSlugs.has(e.slug)) return false;
      if (e.status === 'completed') return false;
      return e.date > todayIso;
    })
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
}

/**
 * Completed events: past events or explicitly marked as completed
 */
export function getCompletedEvents(today = new Date()) {
  const todayIso = today.toISOString().slice(0, 10);
  const ongoingSlugs = new Set(getOngoingEvents(today).map((e) => e.slug));

  return getAllEvents()
    .filter((e) => {
      if (ongoingSlugs.has(e.slug)) return false;
      if (e.status === 'completed') return true;
      const end = e.endDate || e.date;
      return end < todayIso;
    })
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
}

export function getEventBySlug(slug) {
  const event = getAllEvents().find((e) => e.slug === slug);
  if (!event) return undefined;

  const markdown = markdownBySlug[slug];
  return {
    ...event,
    markdownDetails: markdown || null,
  };
}
